import api from './api';

// ─── Types ────────────────────────────────────────────────────────────────────

export type SopCategory = 'SAFETY' | 'OPS' | 'FINANCE' | 'CUSTOMER_SERVICE' | 'GENERAL';
export type VoteDirection = 'UP' | 'DOWN';
export type AnnouncementPriority = 'INFO' | 'WARNING' | 'URGENT';
export type TaskPriorityC = 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';

export interface SopAuthor {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
}

export interface SopAttachmentMeta {
  id: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
}

export interface SopHistoryEntry {
  id: string;
  version: number;
  editedAt: string;
  EditedBy: { firstName: string | null; lastName: string | null; email: string };
}

export interface CeoSopSummary {
  id:             string;
  title:          string;
  body?:          string;
  category:       string;
  tags:           string[];
  isPinned:       boolean;
  version:        number;
  publishedAt:    string | null;
  createdAt:      string;
  updatedAt:      string;
  authorId:       string;
  Author:         SopAuthor;
  ackCount:       number;
  upVotes:        number;
  downVotes:      number;
  myAck:          boolean;
  myVote:         VoteDirection | null;
  attachmentCount: number;
}

export interface CeoSopDetail extends CeoSopSummary {
  body:        string;
  Attachments: SopAttachmentMeta[];
  History:     SopHistoryEntry[];
  Acks:        SopAckItem[];
}

export interface CeoAnnouncementItem {
  id:        string;
  title:     string;
  body:      string;
  priority:  AnnouncementPriority;
  expiresAt: string | null;
  createdAt: string;
  authorId:  string;
  Author:    SopAuthor;
  myAck:     boolean;
}

export interface CeoAnnouncementAckItem {
  id:     string;
  ackedAt: string;
  User:   { id: string; firstName: string | null; lastName: string | null; email: string; photoUrl?: string | null };
}

export interface SopAckItem {
  id:        string;
  createdAt: string;
  User:      { id: string; firstName: string | null; lastName: string | null; email: string; photoUrl?: string | null };
}

export interface CeoTaskUser {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  photoUrl?: string | null;
}

export interface CeoTaskComment {
  id: string;
  body: string;
  createdAt: string;
  Author: SopAuthor;
}

export interface CeoTaskItem {
  id:              string;
  title:           string;
  description?:    string | null;
  priority:        string;
  status:          string;
  dueAt?:          string | null;
  completedAt?:    string | null;
  createdAt:       string;
  sourceType:      string;
  assignedToUserId?: string | null;
  assignedByUserId?: string | null;
  AssignedTo?:     CeoTaskUser | null;
  AssignedBy?:     CeoTaskUser | null;
  Comments?:       CeoTaskComment[];
}

export interface CreateSopPayload {
  title:        string;
  body:         string;
  category?:    string;
  tags?:        string[];
  isPinned?:    boolean;
  publishedAt?: string | null;
  attachments?: Array<{ fileName: string; mimeType: string; fileSize: number; dataBase64: string }>;
}

// ─── Service ──────────────────────────────────────────────────────────────────

export const ceoHubService = {

  // ── SOPs ──────────────────────────────────────────────────────────────────

  async getSops(params?: {
    category?: string;
    tag?: string;
    pinned?: boolean;
    page?: number;
    pageSize?: number;
    q?: string;
  }): Promise<{ items: CeoSopSummary[]; total: number; page: number; pageSize: number; totalPages: number }> {
    const qs = new URLSearchParams();
    if (params?.category) qs.set('category', params.category);
    if (params?.tag)      qs.set('tag', params.tag);
    if (params?.pinned)   qs.set('pinned', 'true');
    if (params?.page)     qs.set('page', String(params.page));
    if (params?.pageSize) qs.set('pageSize', String(params.pageSize));
    if (params?.q)        qs.set('q', params.q);
    const res = await api.get(`/ceo/sops?${qs}`);
    return res.data?.data ?? { items: [], total: 0, page: 1, pageSize: 20, totalPages: 0 };
  },

  async getSop(id: string): Promise<CeoSopDetail> {
    const res = await api.get(`/ceo/sops/${encodeURIComponent(id)}`);
    return res.data?.data;
  },

  async getUnackedCount(): Promise<number> {
    const res = await api.get('/ceo/sops/unacked-count');
    return res.data?.data?.count ?? 0;
  },

  async createSop(payload: CreateSopPayload): Promise<{ id: string }> {
    const res = await api.post('/ceo/sops', payload);
    return res.data?.data;
  },

  async updateSop(id: string, payload: Partial<CreateSopPayload>): Promise<void> {
    await api.put(`/ceo/sops/${encodeURIComponent(id)}`, payload);
  },

  async deleteSop(id: string): Promise<void> {
    await api.delete(`/ceo/sops/${encodeURIComponent(id)}`);
  },

  async acknowledgeSop(id: string): Promise<{ ackCount: number }> {
    const res = await api.post(`/ceo/sops/${encodeURIComponent(id)}/acknowledge`);
    return res.data?.data;
  },

  async voteSop(id: string, vote: VoteDirection | null): Promise<{ upVotes: number; downVotes: number; myVote: VoteDirection | null }> {
    const res = await api.post(`/ceo/sops/${encodeURIComponent(id)}/vote`, { vote });
    return res.data?.data;
  },

  getAttachmentUrl(sopId: string, attId: string): string {
    return `/api/ceo/sops/${encodeURIComponent(sopId)}/attachments/${encodeURIComponent(attId)}`;
  },

  // ── Announcements ────────────────────────────────────────────────────────

  async getAnnouncements(params?: { includeExpired?: boolean }): Promise<CeoAnnouncementItem[]> {
    const qs = params?.includeExpired ? '?includeExpired=true' : '';
    const res = await api.get(`/ceo/announcements${qs}`);
    return res.data?.data?.items ?? [];
  },

  async createAnnouncement(payload: { title: string; body: string; priority: AnnouncementPriority; expiresAt?: string }): Promise<{ id: string }> {
    const res = await api.post('/ceo/announcements', payload);
    return res.data?.data;
  },

  async deleteAnnouncement(id: string): Promise<void> {
    await api.delete(`/ceo/announcements/${encodeURIComponent(id)}`);
  },

  async acknowledgeAnnouncement(id: string): Promise<void> {
    await api.post(`/ceo/announcements/${encodeURIComponent(id)}/acknowledge`);
  },

  async getAnnouncementAcks(id: string): Promise<CeoAnnouncementAckItem[]> {
    const res = await api.get(`/ceo/announcements/${encodeURIComponent(id)}/acks`);
    return res.data?.data?.acks ?? [];
  },

  // ── CEO Tasks ────────────────────────────────────────────────────────────

  async getCeoTasks(params?: { assignedToUserId?: string; status?: string; priority?: string }): Promise<{ items: CeoTaskItem[]; total: number }> {
    const qs = new URLSearchParams();
    if (params?.assignedToUserId) qs.set('assignedToUserId', params.assignedToUserId);
    if (params?.status)           qs.set('status', params.status);
    if (params?.priority)         qs.set('priority', params.priority);
    const res = await api.get(`/ceo/tasks?${qs}`);
    return res.data?.data ?? { items: [], total: 0 };
  },

  async createCeoTask(payload: { title: string; description?: string; assignedToUserId: string; priority?: TaskPriorityC; dueAt?: string }): Promise<CeoTaskItem> {
    const res = await api.post('/ceo/tasks', payload);
    return res.data?.data;
  },

  async updateCeoTask(id: string, payload: { status?: string; priority?: string; dueAt?: string | null }): Promise<void> {
    await api.patch(`/ceo/tasks/${encodeURIComponent(id)}`, payload);
  },

  async addComment(taskId: string, body: string): Promise<CeoTaskComment> {
    const res = await api.post(`/ceo/tasks/${encodeURIComponent(taskId)}/comments`, { body });
    return res.data?.data;
  },
};
